import { useState } from "react";
import { AlertTriangle, CheckCircle2, Scissors, TrendingDown, TrendingUp } from "lucide-react";
import { dec, formatNumber } from "@/lib/formatters";

const n1 = (v) => (v == null ? "—" : dec(v, 1));
const pct = (v) => (v == null ? "—" : `${v > 0 ? "+" : ""}${dec(v, 1)} %`);
const tal = (v) => (v == null ? "—" : `${v > 0 ? "+" : "−"}${formatNumber(Math.abs(v))}`);

export const BEMANNING_NORM = { barn_per_arsverk: 6, barnehagelarar_andel: 43 };

const SEKTORAR = [
  {
    id: "barnehage",
    namn: "Barnehage",
    arsverk: "barnehage_arsverk",
    arsverkNamn: "årsverk til grunnbemanning",
    brukarar: "barnehagebarn",
    brukarNamn: "barn med plass",
    noekkel: "barn_per_arsverk",
    noekkelNamn: "barn per årsverk",
    desimalar: 1,
  },
  {
    id: "skule",
    namn: "Grunnskule",
    arsverk: "laerararsverk",
    arsverkNamn: "lærarårsverk",
    brukarar: "elevar",
    brukarNamn: "elevar",
    noekkel: "elevar_per_laerararsverk",
    noekkelNamn: "elevar per lærarårsverk",
    desimalar: 1,
  },
  {
    id: "omsorg",
    namn: "Helse og omsorg",
    arsverk: "omsorg_arsverk",
    arsverkNamn: "årsverk",
    brukarar: "omsorgsbrukarar",
    brukarNamn: "tenestemottakarar",
    noekkel: "brukarar_per_arsverk_omsorg",
    noekkelNamn: "brukarar per årsverk",
    desimalar: 2,
  },
];

const Delta = ({ verdi, snudd }) => {
  if (verdi == null) return <span className="text-[#8AA4BF]">—</span>;
  const bra = snudd ? verdi < 0 : verdi > 0;
  const Icon = verdi >= 0 ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-1 font-medium ${verdi === 0 ? "text-[#4B647F]" : bra ? "text-[#3F8A86]" : "text-[#C0392B]"}`}>
      <Icon className="w-3 h-3" />
      {pct(verdi)}
    </span>
  );
};

export const BemanningEndring = ({ data }) => {
  const { endring, latest, gruppe_namn, fylke_name } = data;
  if (!endring) return null;
  const rader = SEKTORAR.map((s) => ({ s, a: endring[s.arsverk]?.kommune, b: endring[s.brukarar]?.kommune })).filter((r) => r.a);
  if (rader.length === 0) return null;
  const snittNamn = gruppe_namn ? "Storbyane" : fylke_name || "Fylket";
  const basis = rader[0].a.fra_aar;
  const slutt = rader[0].a.til_aar;

  return (
    <div className="p-4 border border-[#E6E9F0] rounded-xl space-y-3 lg:col-span-2" data-testid="bemanning-endring-block">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF]">Bemanning over tid: {basis}–{slutt}</p>
        <p className="text-xs text-[#8AA4BF] mt-0.5">
          Kor mange årsverk kommunen har fått eller mista, og om brukartalet gjekk same veg. Grønt er fleire årsverk per brukar, raudt er færre.
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid="bemanning-endring-table">
          <thead>
            <tr className="border-b border-[#E6E9F0] text-[#4B647F]">
              <th className="py-2 pr-3 text-left font-medium">Sektor</th>
              <th className="py-2 px-3 text-right font-medium">Årsverk {slutt}</th>
              <th className="py-2 px-3 text-right font-medium">Endring i årsverk</th>
              <th className="py-2 px-3 text-right font-medium">Endring i brukartal</th>
              <th className="py-2 px-3 text-right font-medium">Årsverk {snittNamn}</th>
              <th className="py-2 pl-3 text-right font-medium">Årsverk landet</th>
            </tr>
          </thead>
          <tbody>
            {rader.map(({ s, a, b }) => (
              <tr key={s.id} className="border-b border-[#F1F4F8] text-[#1F2A36]" data-testid={`bemanning-endring-${s.id}`}>
                <td className="py-2 pr-3">
                  <span className="font-medium">{s.namn}</span>
                  <span className="text-[#8AA4BF]"> – {s.arsverkNamn}</span>
                </td>
                <td className="py-2 px-3 text-right tabular-nums">{n1(a.til)}</td>
                <td className="py-2 px-3 text-right tabular-nums">
                  <span className="mr-2 text-[#4B647F]">{tal(a.endring)}</span>
                  <Delta verdi={a.pct} />
                </td>
                <td className="py-2 px-3 text-right tabular-nums">
                  {b ? (
                    <>
                      <span className="mr-2 text-[#4B647F]">{tal(b.endring)}</span>
                      <span className="text-[#4B647F]">{pct(b.pct)}</span>
                    </>
                  ) : (
                    "—"
                  )}
                </td>
                <td className="py-2 px-3 text-right tabular-nums text-[#4B647F]">{pct(endring[s.arsverk]?.snitt?.pct)}</td>
                <td className="py-2 pl-3 text-right tabular-nums text-[#4B647F]">{pct(endring[s.arsverk]?.land?.pct)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-[#8AA4BF]" data-testid="bemanning-endring-tekst">
        {rader
          .map(({ s, a, b }) => {
            const retning = a.endring >= 0 ? "fleire" : "færre";
            const brukar = b ? `${b.endring >= 0 ? "fleire" : "færre"} ${s.brukarNamn} (${pct(b.pct)})` : null;
            return `${s.namn}: ${formatNumber(Math.abs(a.endring))} ${retning} ${s.arsverkNamn} (${pct(a.pct)})${brukar ? ` med ${brukar}` : ""}`;
          })
          .join(". ")}
        . Årsverkstala for helse og omsorg er rekna frå KOSTRA sitt tal på årsverk per 10 000 innbyggjarar, så dei er eit nært anslag.
        {latest?.year && ` Siste år med tal er ${latest.year}.`}
      </p>
    </div>
  );
};

export const Normbrot = ({ data }) => {
  const history = (data.history || []).filter((r) => r.year >= 2019);
  if (!history.some((r) => r.barn_per_arsverk != null)) return null;
  const bemanning = history.filter((r) => r.barn_per_arsverk != null && r.barn_per_arsverk > BEMANNING_NORM.barn_per_arsverk);
  const pedagog = history.filter((r) => r.barnehagelarar_andel != null && r.barnehagelarar_andel < BEMANNING_NORM.barnehagelarar_andel);
  const siste = history[history.length - 1];
  const Linje = ({ brot, tittel, forklaring, testId, verdi }) => (
    <div className="flex items-start gap-2" data-testid={testId}>
      {brot.length > 0 ? (
        <AlertTriangle className="w-4 h-4 text-[#C0392B] mt-0.5 shrink-0" />
      ) : (
        <CheckCircle2 className="w-4 h-4 text-[#3F8A86] mt-0.5 shrink-0" />
      )}
      <div>
        <p className="text-xs text-[#1F2A36]">
          <span className="font-medium">{tittel}:</span>{" "}
          {brot.length > 0 ? (
            <>
              brote i{" "}
              <span className="font-semibold text-[#C0392B]">
                {brot.map((r) => `${r.year} (${verdi(r)})`).join(", ")}
              </span>
            </>
          ) : (
            <span className="text-[#3F8A86] font-medium">innanfor norma alle år frå 2019</span>
          )}
        </p>
        <p className="text-[11px] text-[#8AA4BF]">{forklaring}</p>
      </div>
    </div>
  );
  return (
    <div className="p-4 border border-[#E6E9F0] rounded-xl space-y-3 lg:col-span-2" data-testid="normbrot-block">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF]">Bryt kommunen barnehagenorma?</p>
        <p className="text-xs text-[#8AA4BF] mt-0.5">
          Barnehagelova krev maks 6,0 barn per årsverk til grunnbemanning (bemanningsnorma) og minst 43 prosent barnehagelærarar i
          grunnbemanninga (pedagognorma). Tala gjeld alle barnehagane i kommunen, både kommunale og private.
        </p>
      </div>
      <Linje
        testId="normbrot-bemanning"
        tittel="Bemanningsnorma (maks 6,0 barn per årsverk)"
        forklaring={`Siste år: ${n1(siste?.barn_per_arsverk)} barn per årsverk.`}
        brot={bemanning}
        verdi={(r) => `${dec(r.barn_per_arsverk, 1)} barn`}
      />
      <Linje
        testId="normbrot-pedagog"
        tittel="Pedagognorma (minst 43 % barnehagelærarar)"
        forklaring={`Siste år: ${n1(siste?.barnehagelarar_andel)} % barnehagelærarar av grunnbemanninga.`}
        brot={pedagog}
        verdi={(r) => `${dec(r.barnehagelarar_andel, 1)} %`}
      />
    </div>
  );
};

const KuttRad = ({ s, latest, snitt, land, snittNamn }) => {
  const [kutt, setKutt] = useState(0);
  const arsverk = latest?.[s.arsverk];
  const noekkel = latest?.[s.noekkel];
  if (!arsverk || !noekkel) return null;
  const maks = Math.max(5, Math.round(arsverk * 0.2));
  const nytt = kutt >= arsverk ? null : noekkel * (arsverk / (arsverk - kutt));
  const d = (v) => (v == null ? "—" : dec(v, s.desimalar));
  const norm = s.id === "barnehage" ? BEMANNING_NORM.barn_per_arsverk : null;
  const overNorm = norm != null && nytt != null && nytt > norm;
  return (
    <div className="space-y-2 py-3 border-b border-[#F1F4F8] last:border-0" data-testid={`kutt-${s.id}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-xs font-medium text-[#1F2A36]">
          {s.namn} <span className="text-[#8AA4BF]">· {formatNumber(Math.round(arsverk))} {s.arsverkNamn} i dag</span>
        </p>
        <p className="text-xs text-[#4B647F]">
          Kutt:{" "}
          <span className="font-semibold text-[#C0392B]" data-testid={`kutt-${s.id}-arsverk`}>
            {formatNumber(kutt)} årsverk
          </span>{" "}
          ({dec((kutt / arsverk) * 100, 1)} %)
        </p>
      </div>
      <input
        type="range"
        min={0}
        max={maks}
        step={1}
        value={kutt}
        onChange={(e) => setKutt(Number(e.target.value))}
        className="w-full h-1.5 rounded-full appearance-none bg-[#E6E9F0] accent-[#C0392B] cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-[#C0392B]/30"
        data-testid={`kutt-${s.id}-slider`}
        aria-label={`Kutt i årsverk, ${s.namn}`}
      />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <div className="bg-[#F7F9FC] rounded-lg px-2 py-1.5">
          <p className="text-[10px] text-[#8AA4BF] leading-tight">I dag</p>
          <p className="text-sm text-[#1F2A36] tabular-nums">{d(noekkel)}</p>
        </div>
        <div className={`rounded-lg px-2 py-1.5 ${kutt > 0 ? "bg-[#FBEAE7]" : "bg-[#F7F9FC]"}`}>
          <p className="text-[10px] text-[#8AA4BF] leading-tight">Etter kutt</p>
          <p className={`text-sm tabular-nums font-medium ${overNorm ? "text-[#C0392B]" : "text-[#1F2A36]"}`} data-testid={`kutt-${s.id}-resultat`}>
            {d(nytt)}
          </p>
        </div>
        <div className="bg-[#F7F9FC] rounded-lg px-2 py-1.5">
          <p className="text-[10px] text-[#8AA4BF] leading-tight">{snittNamn}</p>
          <p className="text-sm text-[#1F2A36] tabular-nums">{d(snitt?.[s.noekkel])}</p>
        </div>
        <div className="bg-[#F7F9FC] rounded-lg px-2 py-1.5">
          <p className="text-[10px] text-[#8AA4BF] leading-tight">{norm != null ? "Norm" : "Landet"}</p>
          <p className="text-sm text-[#1F2A36] tabular-nums">{norm != null ? dec(norm, 1) : d(land?.[s.noekkel])}</p>
        </div>
      </div>
      <p className="text-[11px] text-[#8AA4BF]" data-testid={`kutt-${s.id}-tekst`}>
        {kutt === 0
          ? `Dra i linja for å sjå kva eit kutt gjer med ${s.noekkelNamn}.`
          : `Eit kutt på ${formatNumber(kutt)} årsverk gjev ${d(nytt)} ${s.noekkelNamn} – ${dec(nytt - noekkel, s.desimalar)} meir enn i dag${
              overNorm ? ", og over bemanningsnorma på 6,0" : ""
            }.`}
      </p>
    </div>
  );
};

export const Kuttkalkulator = ({ data }) => {
  const latest = data.latest;
  const snitt = data.gruppe || data.fylke;
  const snittNamn = data.gruppe_namn ? "Storbyane" : data.fylke_name || "Fylket";
  if (!latest) return null;
  return (
    <div className="p-4 border border-[#E6E9F0] rounded-xl lg:col-span-2" data-testid="kuttkalkulator-block">
      <div className="flex items-start gap-2 mb-2">
        <Scissors className="w-4 h-4 text-[#8AA4BF] mt-0.5 shrink-0" />
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF]">Kuttkalkulator</p>
          <p className="text-xs text-[#8AA4BF] mt-0.5">
            Kva skjer med barn per vaksen, elevar per lærar og brukarar per årsverk om kommunen tar vekk årsverk? Brukartalet er halde fast, slik
            eit budsjettkutt verkar på kort sikt.
          </p>
        </div>
      </div>
      {SEKTORAR.map((s) => (
        <KuttRad key={s.id} s={s} latest={latest} snitt={snitt} land={data.land} snittNamn={snittNamn} />
      ))}
    </div>
  );
};

export default BemanningEndring;
